import { useTranslation } from 'react-i18next';
import { Gift, Package, RotateCcw, Wallet } from 'lucide-react';
import { formatMoney } from '@/utils/currency';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';

interface CustomerKpiCardsProps {
  totalSales: number;
  orderCount: number;
  outstanding: number;
  advance: number;
  returnsTotal: number;
  loyaltyPoints: number;
  loyaltyValue: number;
}

export default function CustomerKpiCards({
  totalSales,
  orderCount,
  outstanding,
  advance,
  returnsTotal,
  loyaltyPoints,
  loyaltyValue,
}: CustomerKpiCardsProps) {
  const { t } = useTranslation();

  const cards = [
    {
      key: 'sales',
      label: t('customers.totalSales', 'Total Sales'),
      value: formatMoney(totalSales),
      sub: `${orderCount} ${t('customers.dashboard.orders', 'Orders')}`,
      icon: Package,
      wrap: 'bg-violet-50 border-violet-100',
      iconClass: 'text-violet-600 bg-violet-100',
    },
    {
      key: 'outstanding',
      label: t('customers.outstanding', 'Outstanding'),
      value: formatMoney(outstanding),
      sub: outstanding > 0 ? t('customers.dashboard.due', 'Due') : t('customers.dashboard.clear', 'Clear'),
      icon: PakRupeeIcon,
      wrap: outstanding > 0 ? 'bg-rose-50 border-rose-100' : 'bg-slate-50 border-slate-100',
      iconClass: outstanding > 0 ? 'text-rose-600 bg-rose-100' : 'text-slate-500 bg-slate-100',
      valueClass: outstanding > 0 ? 'text-rose-700' : undefined,
    },
    {
      key: 'advance',
      label: t('suppliers.advance', 'Advance'),
      value: formatMoney(advance),
      sub: t('customers.dashboard.creditBalance', 'Credit balance'),
      icon: Wallet,
      wrap: 'bg-emerald-50 border-emerald-100',
      iconClass: 'text-emerald-600 bg-emerald-100',
    },
    {
      key: 'returns',
      label: t('customers.dashboard.returns', 'Returns'),
      value: formatMoney(returnsTotal),
      sub: t('customers.dashboard.refunded', 'Refunded'),
      icon: RotateCcw,
      wrap: 'bg-amber-50 border-amber-100',
      iconClass: 'text-amber-600 bg-amber-100',
    },
    {
      key: 'loyalty',
      label: t('customers.dashboard.loyaltyPoints', 'Loyalty Points'),
      value: `${Math.floor(loyaltyPoints)} pts`,
      sub: formatMoney(loyaltyValue),
      icon: Gift,
      wrap: 'bg-sky-50 border-sky-100',
      iconClass: 'text-sky-600 bg-sky-100',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div
            key={c.key}
            className={`rounded-2xl border p-3.5 shadow-sm ${c.wrap}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {c.label}
              </p>
              <span className={`p-1.5 rounded-lg ${c.iconClass}`}>
                <Icon className="w-3.5 h-3.5" />
              </span>
            </div>
            <p className={`text-lg font-bold tabular-nums text-slate-900 ${c.valueClass || ''}`}>
              {c.value}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">{c.sub}</p>
          </div>
        );
      })}
    </div>
  );
}
